import { Button, Icon, Text, TextButton, TextInput } from "@components/library";
import { COLORS, DISCOVER, FONTS, STYLES, Z_INDICES } from "@constants";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { useEffect, useState } from "react";
import styled from "styled-components";
import type { DiscoverFeedInterestsVariant } from "./DiscoverFeedInterests";

const CONFIGS = {
  add: {
    title: "Add interest",
    infoText: "Describe your solution or partner of interest in 2-3 sentences.",
    placeholder:
      "E.g. Edible biocoatings for fresh fruit and veggies, with FDA approval for use in the United States and Canada.",
    buttonText: "Save",
    width: 592,
    startingHeight: 148,
    rows: 6,
  },
  edit: {
    title: "Edit interest",
    infoText: "Tell us more about this interest to improve recommendations.",
    placeholder: undefined,
    buttonText: "Save",
    width: 524,
    startingHeight: 175,
    rows: 7,
  },
} as const;

interface BaseInterestFlyoutProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  mode: "add" | "edit";
  variant?: DiscoverFeedInterestsVariant;
  initialValue?: string;
  lastEdited?: string;
  isSubmitLoading?: boolean;
  isRemoveLoading?: boolean;
  onClose: () => void;
  onSubmit: (text: string) => Promise<unknown> | unknown;
  onRemove?: () => Promise<unknown> | unknown;
}

const InterestFlyout = ({
  isOpen,
  anchorElement,
  mode,
  variant,
  initialValue = "",
  lastEdited,
  isSubmitLoading = false,
  isRemoveLoading = false,
  onClose,
  onSubmit,
  onRemove,
}: BaseInterestFlyoutProps) => {
  const [interestText, setInterestText] = useState(initialValue);

  const { x, y, refs, strategy } = useFloating({
    open: isOpen,
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "edit") setInterestText(initialValue);
    else setInterestText("");
  }, [initialValue, mode, isOpen]);

  useEffect(() => {
    if (anchorElement) refs.setReference(anchorElement);
  }, [anchorElement, refs]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const targetNode = event.target as Node;
      const floatingElement = refs.floating;

      if (floatingElement.current?.contains(targetNode)) return;
      if (anchorElement?.contains(targetNode)) return;

      onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [anchorElement, isOpen, onClose, refs]);

  if (!isOpen) return null;

  const isLoading = isSubmitLoading || isRemoveLoading;
  const isSubmitDisabled = !interestText.trim() || isLoading;

  const { width, title, infoText, placeholder, rows, startingHeight, buttonText } = CONFIGS[mode];

  return (
    <FlyoutContainer
      ref={refs.setFloating}
      style={{
        position: strategy,
        top: y,
        left: x,
      }}
      $width={width}
    >
      <Container>
        <ContentSection>
          <InputFieldSection>
            <Label>{title}</Label>
            <InfoBanner>
              <IconFrame>
                <Icon name="Info Small" size="xs" color={COLORS.NEUTRAL_800} />
              </IconFrame>
              <InfoText>{infoText}</InfoText>
            </InfoBanner>
            <TextInput
              value={interestText}
              onChange={(e) => setInterestText(e.target.value)}
              textarea
              rows={rows}
              placeholder={placeholder}
              resize="vertical"
              startingHeight={`${startingHeight}px`}
              hideClearButton
              maxLength={DISCOVER.MAX_INTEREST_LENGTH}
            />
            {lastEdited && <LastEdited>Last edited: {lastEdited}</LastEdited>}
          </InputFieldSection>
        </ContentSection>
        <Footer $hasRemoveButton={mode === "edit"}>
          {mode === "edit" && (
            <TextButton
              text="Remove interest"
              iconName="Delete"
              iconPosition="left"
              color={COLORS.RED_700}
              size="md"
              onClick={onRemove}
              disabled={isLoading}
            />
          )}
          <ButtonGroup>
            {isSubmitLoading ? (
              <SavedState>
                <Icon name="Success" size="md" color={COLORS.GREEN_800} />
                <Text font={FONTS.MEDIUM_3} color={COLORS.GREEN_800}>
                  Saved{variant === "feed" ? ". Included in next feed update." : "."}
                </Text>
              </SavedState>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => onSubmit(interestText)}
                  disabled={isSubmitDisabled}
                >
                  {buttonText}
                </Button>
              </>
            )}
          </ButtonGroup>
        </Footer>
      </Container>
    </FlyoutContainer>
  );
};

export default InterestFlyout;

const FlyoutContainer = styled.div<{ $width: number }>`
  position: absolute;
  width: ${({ $width }) => $width}px;
  z-index: ${Z_INDICES.DISCOVER_FEED_INTEREST_FLYOUT};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: ${COLORS.WHITE};
  border: 1px solid ${COLORS.NEUTRAL_300};
  border-radius: 4px;
  box-shadow: ${STYLES.SHADOW_B};
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputFieldSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  ${FONTS.SEMIBOLD_2};
  color: ${COLORS.NEUTRAL_800};
`;

const InfoBanner = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 16px;
  background-color: ${COLORS.NEUTRAL_100};
  border-radius: 6px;
`;

const IconFrame = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 2px 0;
`;

const InfoText = styled.p`
  flex: 1;
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_800};
  margin: 0;
  white-space: pre-wrap;
`;

const LastEdited = styled.p`
  ${FONTS.REGULAR_3};
  color: ${COLORS.NEUTRAL_500};
  margin: 0;
`;

const Footer = styled.div<{ $hasRemoveButton: boolean }>`
  display: flex;
  justify-content: ${({ $hasRemoveButton }) => ($hasRemoveButton ? "space-between" : "flex-end")};
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SavedState = styled.div`
  display: flex;
  align-items: center;
`;
